<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant, hasActiveSubscription } from '@/composables/useTenant';
import { useAuth } from '@/composables/useAuth';
import { billingApi } from '@/services/api';
import {
  mountStripeElements,
  confirmStripeSetupIntent,
  confirmStripePaymentOrSetup,
  redirectToStripeCheckout,
  STRIPE_PUBLISHABLE_KEY,
  type StripeMountResult
} from '@/services/stripeService';
import type {
  ServiceId,
  PaymentType,
  ServicePlan,
  PlayerPaymentRecord,
  TenantPaymentMethod,
  TenantSubscriptionItem
} from '@/types/models';
import AppBadge from '@/components/ui/AppBadge.vue';
import AppModal from '@/components/ui/AppModal.vue';
import SeatAllocatorModal from '@/components/SeatAllocatorModal.vue';
import InvoiceTable from '@/components/InvoiceTable.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NButton, NSelect, NSpin, NTag, useMessage } from 'naive-ui';
import {
  CreditCard,
  ShieldCheck,
  Video,
  Dumbbell,
  CheckCircle2,
  AlertTriangle,
  Receipt,
  ExternalLink,
  Sparkles,
  Lock,
  Building2,
  Users,
  Copy,
  Layers,
  ArrowUpRight,
  RefreshCw,
  UserCheck,
  RotateCcw,
  Clock,
} from 'lucide-vue-next';

const { t } = useI18n();
const naiveMsg = useMessage();
const {
  activeTenant,
  currentUser,
  managedTeams,
  currentOrgTeams,
  setServicePaymentType,
  toggleTeamService
} = useTenant();
const {
  isAdmin,
  isCoach,
  isPlayer,
  isGuardian,
  canManageSubscriptions,
  canChangePaymentMethod,
  canAllocateSeats,
  canUpgradePlan,
} = useAuth();
const { notifySuccess, notifyError, notifyInfo } = useToastNotification();

// Active selected team
const selectedTeamId = ref(currentOrgTeams.value[0]?.id || managedTeams.value[0]?.id || 'team_tokyo_gas_top');

// Reactive state fetched from billing API
const services = ref<ServicePlan[]>([]);
const playerPayments = ref<PlayerPaymentRecord[]>([]);
const activeSubscriptions = ref<TenantSubscriptionItem[]>([]);
const paymentMethod = ref<TenantPaymentMethod>({
  hasPaymentMethod: false,
});
const stripeCustomerId = ref<string>('');

const isLoadingBilling = ref(true);
const isOpeningPortal = ref(false);
const isSettingUpPaymentMethod = ref(false);
const isSubscribingService = ref<Record<string, boolean>>({});
const isCancelingService = ref<Record<string, boolean>>({});
const isResumingService = ref<Record<string, boolean>>({});

// Seat Allocator Modal State
const isSeatAllocatorOpen = ref(false);
const selectedServiceForSeats = ref<ServiceId>('VIDEO_ANALYTICS');
const selectedServiceNameForSeats = ref<string>('Video Analytics & AI Replay');

const openSeatAllocator = (service: ServicePlan) => {
  selectedServiceForSeats.value = service.id;
  selectedServiceNameForSeats.value = service.name;
  isSeatAllocatorOpen.value = true;
};

// Pending service when subscribing without payment method set
const pendingServiceToSubscribe = ref<ServicePlan | null>(null);

// Payment method setup modal state
const isStripeSetupModalOpen = ref(false);

// Stripe Elements live mount state
const stripeMount = ref<StripeMountResult | null>(null);
const isMountingStripeElements = ref(false);
const stripeSetupClientSecret = ref<string>('');
const stripeSetupSessionId = ref<string>('');
const stripeSetupUrl = ref<string>('');
const stripeMountError = ref<string>('');

// Fetch all billing data from backend API
const loadBillingData = async () => {
  isLoadingBilling.value = true;
  try {
    const [subData, pmData, fetchedServices, fetchedPayments] = await Promise.all([
      billingApi.getTenantSubscription(),
      billingApi.getPaymentMethod(),
      billingApi.getServices(),
      billingApi.getPlayerPayments(activeTeam.value?.id, currentUser.value?.id),
    ]);

    if (subData) {
      if (Array.isArray(subData.subscriptions)) {
        activeSubscriptions.value = subData.subscriptions;
      }
      if (subData.stripeCustomerId) {
        stripeCustomerId.value = subData.stripeCustomerId;
      }
      if (subData.paymentMethod && subData.paymentMethod.hasPaymentMethod) {
        paymentMethod.value = subData.paymentMethod;
      }
    }

    if (pmData && pmData.hasPaymentMethod) {
      paymentMethod.value = pmData;
      if (pmData.stripeCustomerId) {
        stripeCustomerId.value = pmData.stripeCustomerId;
      }
    }

    if (fetchedServices && fetchedServices.length > 0) {
      services.value = fetchedServices;
    }

    if (fetchedPayments && fetchedPayments.length > 0) {
      playerPayments.value = fetchedPayments;
    }
  } catch (err) {
    console.warn('[BillingView] Notice loading billing data from API:', err);
  } finally {
    isLoadingBilling.value = false;
  }
};

onMounted(() => {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('setup') === 'success') {
      notifySuccess('Payment Method Updated', 'Your Stripe payment method has been verified and registered.');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('setup') === 'cancel') {
      notifyInfo('Payment Setup Canceled', 'No changes were made to your payment method.');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('session_id')) {
      notifySuccess('Payment Confirmed', 'Your Stripe Checkout payment was processed successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  loadBillingData();
});

watch(currentOrgTeams, (newTeams) => {
  if (newTeams && newTeams.length > 0) {
    if (!newTeams.some(t => t.id === selectedTeamId.value)) {
      selectedTeamId.value = newTeams[0].id;
    }
  }
}, { immediate: true });

const activeTeam = computed(() => {
  const match = managedTeams.value.find(t => t.id === selectedTeamId.value);
  if (match) return match;
  if (currentOrgTeams.value.length > 0) return currentOrgTeams.value[0];
  return managedTeams.value[0];
});

// Check if a service is active or pending cancellation for this tenant/team
const getServiceSubscription = (serviceId: ServiceId) => {
  return activeSubscriptions.value.find(s => s.serviceId === serviceId);
};

const isServiceActiveForTeam = (serviceId: ServiceId) => {
  const backendSub = getServiceSubscription(serviceId);
  if (backendSub) return backendSub.status === 'ACTIVE' || backendSub.status === 'cancel_wait';

  const teamSub = activeTeam.value?.subscriptions.find(s => s.serviceId === serviceId);
  return teamSub ? teamSub.status === 'ACTIVE' || teamSub.status === 'cancel_wait' : false;
};

// Check if subscription is in cancel_wait status
const isServiceCancelWait = (serviceId: ServiceId) => {
  const backendSub = getServiceSubscription(serviceId);
  return backendSub?.status === 'cancel_wait';
};

// Get current payment type (TEAM vs INDIVIDUAL)
const getServicePaymentType = (serviceId: ServiceId): PaymentType => {
  const backendSub = getServiceSubscription(serviceId);
  if (backendSub) return backendSub.payerType;

  const teamSub = activeTeam.value?.subscriptions.find(s => s.serviceId === serviceId);
  if (teamSub?.paymentType) return teamSub.paymentType;
  if (teamSub?.payerType) return teamSub.payerType;

  if (serviceId === 'VIDEO_ANALYTICS' || serviceId === 'BASEBALL_OPS_CORE') return 'TEAM';
  return activeTeam.value?.paymentType || 'TEAM';
};

const isTeamOnlyService = (service: ServicePlan) => {
  return (
    service.id === 'VIDEO_ANALYTICS' ||
    service.id === 'BASEBALL_OPS_CORE' ||
    (service.allowedPaymentTypes.length === 1 && service.allowedPaymentTypes[0] === 'TEAM')
  );
};

// Handle Payment Type selection for a specific service
const handleServicePaymentTypeChange = async (serviceId: ServiceId, paymentType: PaymentType) => {
  if (!isAdmin.value) {
    naiveMsg.warning('Coaches cannot modify billing payment models.');
    return;
  }

  if ((serviceId === 'VIDEO_ANALYTICS' || serviceId === 'BASEBALL_OPS_CORE') && paymentType === 'INDIVIDUAL') {
    notifyError(
      t('billing.videoAnalyticsRule'),
      'This service requires TEAM payment and cannot be configured for individual player billing.'
    );
    return;
  }

  try {
    await setServicePaymentType(activeTeam.value.id, serviceId, paymentType);
    await billingApi.configureService(activeTeam.value.id, serviceId, paymentType);

    const sub = activeSubscriptions.value.find(s => s.serviceId === serviceId);
    if (sub) {
      sub.payerType = paymentType;
    }

    notifySuccess(
      'Payment Model Updated',
      `${serviceId.replace(/_/g, ' ')} is now set to ${paymentType === 'TEAM' ? 'Team Payment' : 'Individual Player Payment'}.`
    );
  } catch (err: any) {
    notifyError('Update Failed', err.message || 'Could not update payment configuration.');
  }
};

// =========================================================================
// STRIPE ELEMENTS INITIALIZATION & SETUP
// =========================================================================
const initializeStripeElements = async () => {
  isMountingStripeElements.value = true;
  stripeMountError.value = '';

  try {
    const baseUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : 'https://app.nextbasecloud.com/dashboard/billing';
    const setupData = await billingApi.setupPaymentMethod({
      returnUrl: baseUrl,
      successUrl: `${baseUrl}?setup=success`,
      cancelUrl: `${baseUrl}?setup=cancel`,
    });

    if (setupData) {
      stripeSetupClientSecret.value = setupData.clientSecret || '';
      stripeSetupSessionId.value = setupData.sessionId || '';
      stripeSetupUrl.value = setupData.setupUrl || setupData.url || setupData.portalUrl || '';

      await nextTick();
      let container = document.getElementById('stripe-payment-element-mount');
      if (!container) {
        await new Promise((res) => setTimeout(res, 150));
        container = document.getElementById('stripe-payment-element-mount');
      }

      if (container && setupData.clientSecret) {
        const mounted = await mountStripeElements(container, setupData.clientSecret);
        stripeMount.value = mounted;
      }
    }
  } catch (e: any) {
    console.warn('[BillingView] Stripe elements setup notice:', e);
    stripeMountError.value = e.message || 'Failed to initialize Stripe Payment Element.';
  } finally {
    isMountingStripeElements.value = false;
  }
};

const openPaymentMethodSetupModal = (service?: ServicePlan) => {
  if (!canChangePaymentMethod.value) {
    naiveMsg.warning('Coaches cannot modify tenant payment methods.');
    return;
  }
  pendingServiceToSubscribe.value = service || null;
  isStripeSetupModalOpen.value = true;
};

watch(isStripeSetupModalOpen, (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      initializeStripeElements();
    });
  } else {
    if (stripeMount.value?.paymentElement) {
      try {
        stripeMount.value.paymentElement.destroy?.();
      } catch (e) {}
    }
    stripeMount.value = null;
    stripeMountError.value = '';
  }
});

const launchHostedStripeSetup = async () => {
  if (stripeSetupSessionId.value || stripeSetupUrl.value) {
    await redirectToStripeCheckout(stripeSetupSessionId.value, stripeSetupUrl.value);
  } else {
    isSettingUpPaymentMethod.value = true;
    try {
      const baseUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : 'https://app.nextbasecloud.com/dashboard/billing';
      const setup = await billingApi.setupPaymentMethod({
        returnUrl: baseUrl,
        successUrl: `${baseUrl}?setup=success`,
        cancelUrl: `${baseUrl}?setup=cancel`,
      });
      if (setup?.setupUrl || setup?.url || setup?.sessionId) {
        await redirectToStripeCheckout(setup.sessionId, setup.setupUrl || setup.url);
      }
    } catch (e: any) {
      notifyError('Stripe Error', e.message || 'Failed to redirect to Stripe Checkout.');
    } finally {
      isSettingUpPaymentMethod.value = false;
    }
  }
};

const handleConfirmStripeSetup = async () => {
  isSettingUpPaymentMethod.value = true;

  try {
    if (stripeMount.value) {
      const stripeResult = await confirmStripePaymentOrSetup(stripeMount.value);
      if (!stripeResult.success && stripeResult.error) {
        notifyError('Stripe Setup Error', stripeResult.error);
        isSettingUpPaymentMethod.value = false;
        return;
      }
    } else if (stripeSetupUrl.value) {
      await launchHostedStripeSetup();
      return;
    }

    isStripeSetupModalOpen.value = false;
    notifySuccess('Payment Method Saved', 'Stripe verified payment method successfully.');

    await loadBillingData();

    if (pendingServiceToSubscribe.value) {
      await executeTeamSubscription(pendingServiceToSubscribe.value);
    }
  } catch (err: any) {
    notifyError('Setup Error', err.message || 'Failed to confirm Stripe payment method.');
  } finally {
    isSettingUpPaymentMethod.value = false;
  }
};

// =========================================================================
// SUBSCRIPTION LIFECYCLE: CANCEL (cancel_wait) & RESUME
// =========================================================================
const handleCancelSubscription = async (service: ServicePlan) => {
  if (!isAdmin.value) {
    naiveMsg.warning('Coaches cannot modify subscription contracts.');
    return;
  }

  if (service.isMandatory) {
    notifyError(t('billing.coreRequired'), t('billing.coreRequiredMsg'));
    return;
  }

  isCancelingService.value[service.id] = true;
  try {
    // POST /api/v1/billing/subscriptions/:id/cancel
    const updated = await billingApi.cancelSubscription(service.id);
    const subIdx = activeSubscriptions.value.findIndex(s => s.serviceId === service.id);
    if (subIdx !== -1) {
      activeSubscriptions.value[subIdx] = updated;
    }
    naiveMsg.info(`Subscription transitioned to cancel_wait. Active until period end.`);
  } catch (err: any) {
    notifyError('Cancellation Error', err.message || 'Failed to initiate cancellation.');
  } finally {
    isCancelingService.value[service.id] = false;
  }
};

const handleResumeSubscription = async (service: ServicePlan) => {
  if (!isAdmin.value) {
    naiveMsg.warning('Coaches cannot modify subscription contracts.');
    return;
  }

  isResumingService.value[service.id] = true;
  try {
    // POST /api/v1/billing/subscriptions/:id/resume
    const updated = await billingApi.resumeSubscription(service.id);
    const subIdx = activeSubscriptions.value.findIndex(s => s.serviceId === service.id);
    if (subIdx !== -1) {
      activeSubscriptions.value[subIdx] = updated;
    }
    naiveMsg.success(`Subscription resumed successfully. Service remains fully active.`);
  } catch (err: any) {
    notifyError('Resume Error', err.message || 'Failed to resume subscription.');
  } finally {
    isResumingService.value[service.id] = false;
  }
};

// =========================================================================
// SUBSCRIBE VIA STRIPE (TEAM) FLOW
// =========================================================================
const handleTeamAdminSubscribe = async (service: ServicePlan) => {
  if (!isAdmin.value) {
    naiveMsg.warning('Coaches cannot upgrade billing plans or initiate subscriptions.');
    return;
  }

  const active = isServiceActiveForTeam(service.id);
  const isPendingCancel = isServiceCancelWait(service.id);

  if (isPendingCancel) {
    await handleResumeSubscription(service);
    return;
  }

  if (active) {
    await handleCancelSubscription(service);
    return;
  }

  const pType = getServicePaymentType(service.id);
  if (pType === 'TEAM' && !paymentMethod.value.hasPaymentMethod) {
    openPaymentMethodSetupModal(service);
    return;
  }

  await executeTeamSubscription(service);
};

const executeTeamSubscription = async (service: ServicePlan) => {
  isSubscribingService.value[service.id] = true;
  const pType = getServicePaymentType(service.id);

  try {
    const res = await billingApi.subscribeTeam(activeTeam.value.id, service.id);
    await toggleTeamService(activeTeam.value.id, service.id, true, pType);

    if (res?.sessionId || res?.checkoutUrl || res?.setupUrl || res?.url) {
      const checkoutUrl = res.checkoutUrl || res.setupUrl || res.url;
      await redirectToStripeCheckout(res.sessionId, checkoutUrl);
      notifySuccess('Stripe Checkout Launched', `Redirecting to Stripe to confirm ${service.name}...`);
    } else {
      notifySuccess('Subscription Activated', `${service.name} has been activated for ${activeTenant.value.name}.`);
    }

    await loadBillingData();
  } catch (err: any) {
    notifyError('Subscription Error', err.message || 'Failed to process subscription via Stripe.');
  } finally {
    isSubscribingService.value[service.id] = false;
    pendingServiceToSubscribe.value = null;
  }
};

const openStripeCustomerPortal = async (flow = 'payment_method_update') => {
  if (!isAdmin.value) {
    naiveMsg.warning('Access restricted: Coaches cannot access the Tenant Billing Portal.');
    return;
  }

  isOpeningPortal.value = true;
  try {
    const returnUrl = typeof window !== 'undefined' ? window.location.href : 'https://app.nextbasecloud.com/dashboard/billing';
    const session = await billingApi.createPortalSession({ returnUrl, flow });

    if (session?.portalUrl || session?.url) {
      const url = session.portalUrl || session.url;
      window.open(url, '_blank', 'noopener,noreferrer');
      notifySuccess('Stripe Customer Portal Opened', 'Opening Stripe hosted portal in a new tab.');
    } else {
      notifyError('Portal Error', 'Unable to generate Stripe Customer Portal session.');
    }
  } catch (err: any) {
    notifyError('Portal Error', err.message || 'Failed to launch Stripe Customer Portal.');
  } finally {
    isOpeningPortal.value = false;
  }
};

// Share Paylink Modal State
const isPaylinkModalOpen = ref(false);
const generatedPaylink = ref('');

const openPaylinkGenerator = () => {
  generatedPaylink.value = `https://checkout.stripe.com/pay/cs_live_${activeTeam.value.id}_dues2025`;
  isPaylinkModalOpen.value = true;
};

const copyGeneratedLink = () => {
  navigator.clipboard.writeText(generatedPaylink.value);
  notifySuccess('Stripe Link Copied', 'Payment link copied to clipboard.');
};

// Player/Guardian Checkout Modal State
const isPlayerStripeCheckoutOpen = ref(false);
const selectedServiceToPay = ref<ServicePlan | null>(null);
const playerPaymentProcessing = ref(false);

const openPlayerCheckout = (service: ServicePlan) => {
  selectedServiceToPay.value = service;
  isPlayerStripeCheckoutOpen.value = true;
};

const processPlayerStripePayment = async () => {
  playerPaymentProcessing.value = true;
  try {
    if (selectedServiceToPay.value) {
      // Mandate: Force billing_interval = "MONTHLY" in all individual checkout flows
      const res = await billingApi.createCheckoutSession({
        serviceId: selectedServiceToPay.value.id,
        teamId: activeTeam.value.id,
        playerId: currentUser.value.id,
      });

      if (res?.sessionId || res?.checkoutUrl) {
        await redirectToStripeCheckout(res.sessionId, res.checkoutUrl);
      }

      playerPayments.value.unshift({
        id: `pay_${Date.now()}`,
        playerId: currentUser.value.id,
        serviceId: selectedServiceToPay.value.id,
        amount: selectedServiceToPay.value.priceMonthly,
        currency: 'USD',
        status: 'PAID',
        date: new Date().toISOString().split('T')[0],
        paymentMethod: 'Stripe Payment (Monthly)',
      });
    }
    notifySuccess(t('billing.paymentSuccess'), t('billing.paymentSuccessMsg', { service: selectedServiceToPay.value?.name }));
  } catch (err: any) {
    notifyError('Payment Failed', err.message);
  } finally {
    playerPaymentProcessing.value = false;
    isPlayerStripeCheckoutOpen.value = false;
  }
};

const teamOptions = computed(() =>
  currentOrgTeams.value.map(t => ({ label: t.name, value: t.id }))
);
</script>

<template>
  <div class="space-y-8 animate-in fade-in duration-200">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
      <div>
        <div class="flex items-center gap-2">
          <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
            {{ isAdmin ? 'Tenant Billing & Subscriptions' : isCoach ? 'Team Services & Seat Allocations' : 'Member Dues & Services' }}
          </h1>
          <AppBadge variant="accent" pill>
            <span class="flex items-center gap-1">
              <ShieldCheck class="w-3.5 h-3.5 text-emerald-400" /> Stripe API Connected
            </span>
          </AppBadge>
        </div>
        <p class="mt-1 text-xs sm:text-sm text-slate-400">
          {{ isAdmin ? t('billing.teamAdminSubtitle', { name: activeTenant.name }) : isCoach ? 'Inspect team service statuses and allocate contracted user seats to athletes.' : t('billing.memberSubtitle') }}
        </p>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-wrap items-center gap-3">
        <!-- Stripe Customer Portal direct button for Admin ONLY -->
        <NButton
          v-if="isAdmin"
          type="primary"
          secondary
          :loading="isOpeningPortal"
          @click="openStripeCustomerPortal('payment_method_update')"
          class="font-semibold text-xs shadow-sm"
        >
          <template #icon>
            <ExternalLink class="w-3.5 h-3.5 text-sky-400" />
          </template>
          Stripe Customer Portal
        </NButton>

        <NButton
          v-if="isAdmin"
          secondary
          @click="openPaylinkGenerator"
          class="font-semibold text-xs"
        >
          <template #icon>
            <ArrowUpRight class="w-4 h-4 text-emerald-400" />
          </template>
          Share Stripe Paylink
        </NButton>

        <div class="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-slate-400">
          <CreditCard class="w-4 h-4 text-sky-400" />
          <span>Stripe Key: <strong class="font-mono text-slate-300">{{ STRIPE_PUBLISHABLE_KEY.slice(0, 8) }}...</strong></span>
        </div>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- ADMIN & COACH VIEW -->
    <!-- ========================================================================= -->
    <div v-if="isAdmin || isCoach" class="space-y-8">
      <!-- COACH RESTRICTION BANNER -->
      <div
        v-if="isCoach"
        class="p-4 rounded-2xl bg-sky-500/10 border border-sky-500/30 flex items-start gap-3 shadow-lg"
      >
        <div class="w-8 h-8 rounded-xl bg-sky-500/20 text-sky-400 flex items-center justify-center shrink-0">
          <ShieldCheck class="w-4 h-4" />
        </div>
        <div class="text-xs text-slate-300 leading-relaxed">
          {{ t('billing.coachRestrictionNotice') }}
        </div>
      </div>

      <!-- 1. ACTIVATION STATUS BANNER FOR ADMIN -->
      <div
        v-if="isAdmin && !hasActiveSubscription && activeSubscriptions.length === 0"
        class="p-5 rounded-3xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-4 shadow-xl"
      >
        <div class="w-12 h-12 rounded-2xl bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 border border-amber-500/30">
          <AlertTriangle class="w-6 h-6" />
        </div>
        <div class="space-y-1">
          <h3 class="text-sm font-extrabold text-amber-300 uppercase tracking-wider">
            {{ t('billing.setupRequiredTitle') }}
          </h3>
          <p class="text-xs text-slate-300 leading-relaxed">
            {{ t('billing.setupRequiredDesc', { org: activeTenant.name }) }}
          </p>
        </div>
      </div>

      <!-- 2. MASTER PAYMENT METHOD CARD & STRIPE PORTAL (ADMIN ONLY) -->
      <div v-if="isAdmin" class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Master Payment Method on File -->
        <div class="lg:col-span-2 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-800 rounded-3xl p-6 shadow-xl relative overflow-hidden flex flex-col justify-between">
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2.5">
                <div class="w-10 h-10 rounded-2xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-400">
                  <CreditCard class="w-5 h-5" />
                </div>
                <div>
                  <h3 class="text-sm font-extrabold text-slate-100 uppercase tracking-wider">
                    {{ t('billing.paymentMethodTitle') }}
                  </h3>
                  <p class="text-xs text-slate-400">
                    {{ t('billing.paymentMethodSubtitle') }}
                  </p>
                </div>
              </div>

              <AppBadge v-if="paymentMethod.hasPaymentMethod" variant="success" pill>
                <span class="flex items-center gap-1">
                  <CheckCircle2 class="w-3 h-3 text-emerald-400" /> {{ t('billing.defaultMethod') }}
                </span>
              </AppBadge>
            </div>

            <!-- PAYMENT METHOD ON RECORD -->
            <div
              v-if="paymentMethod.hasPaymentMethod"
              class="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            >
              <div class="flex items-center gap-4">
                <div class="w-12 h-9 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center text-white font-bold text-xs uppercase tracking-widest shadow-md">
                  {{ paymentMethod.brand || 'CARD' }}
                </div>
                <div>
                  <div class="font-mono text-sm font-bold text-slate-100 flex items-center gap-2">
                    <span>•••• •••• •••• {{ paymentMethod.last4 || '4242' }}</span>
                    <span class="text-[10px] text-sky-400 font-sans font-bold bg-sky-500/10 px-2 py-0.5 rounded border border-sky-500/20">Stripe</span>
                  </div>
                  <div class="text-[11px] text-slate-400 flex items-center gap-3 mt-0.5">
                    <span v-if="paymentMethod.expMonth && paymentMethod.expYear">
                      Expires {{ paymentMethod.expMonth }}/{{ paymentMethod.expYear }}
                    </span>
                    <span v-if="stripeCustomerId || paymentMethod.stripeCustomerId" class="text-slate-500 font-mono">
                      {{ stripeCustomerId || paymentMethod.stripeCustomerId }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <NButton
                  size="small"
                  secondary
                  @click="openPaymentMethodSetupModal()"
                  class="font-semibold text-xs"
                >
                  {{ t('billing.changePaymentMethod') }}
                </NButton>
              </div>
            </div>

            <!-- NO PAYMENT METHOD SET YET -->
            <div
              v-else
              class="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3"
            >
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <span class="text-xs font-bold text-amber-300 flex items-center gap-2">
                  <AlertTriangle class="w-4 h-4 text-amber-400 shrink-0" />
                  {{ t('billing.noCardOnFile') }}
                </span>
                <NButton
                  size="small"
                  type="primary"
                  @click="openPaymentMethodSetupModal()"
                  class="font-bold text-xs shadow-md"
                >
                  + Set Up Payment Method via Stripe
                </NButton>
              </div>

              <p class="text-[11px] text-slate-400 leading-relaxed">
                Stripe supports Credit/Debit Cards (Visa, MC, JCB, Amex), Bank Transfer (振込 / Furikomi virtual accounts), and Konbini convenience store payments.
              </p>
            </div>
          </div>

          <div class="mt-4 pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
            <span class="flex items-center gap-1.5">
              <Lock class="w-3.5 h-3.5 text-emerald-400" />
              Direct 256-bit tokenized checkout via official Stripe.js API
            </span>
            <span v-if="stripeCustomerId" class="font-mono text-slate-400">
              Customer: <strong class="text-slate-300">{{ stripeCustomerId }}</strong>
            </span>
          </div>
        </div>

        <!-- Stripe Customer Portal Quick Box -->
        <div class="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl flex flex-col justify-between space-y-4">
          <div class="space-y-2">
            <div class="flex items-center gap-2 text-sky-400">
              <ExternalLink class="w-5 h-5" />
              <h4 class="text-sm font-bold text-slate-100">Stripe Customer Portal</h4>
            </div>
            <p class="text-xs text-slate-400 leading-relaxed">
              Access Stripe's secure self-service portal to download tax invoices, view past receipts, add backup cards, or manage billing addresses.
            </p>
          </div>

          <div class="space-y-2 pt-2 border-t border-slate-800">
            <NButton
              block
              type="primary"
              :loading="isOpeningPortal"
              @click="openStripeCustomerPortal('payment_method_update')"
              class="font-bold text-xs shadow-lg shadow-sky-500/20"
            >
              <template #icon>
                <ArrowUpRight class="w-4 h-4" />
              </template>
              Launch Hosted Stripe Portal
            </NButton>

            <NButton
              block
              quaternary
              size="small"
              @click="openStripeCustomerPortal('history')"
              class="text-xs text-slate-400 hover:text-slate-200"
            >
              <template #icon>
                <Receipt class="w-3.5 h-3.5" />
              </template>
              Manage Stripe Billing History
            </NButton>
          </div>
        </div>
      </div>

      <!-- 3. ACTIVE SUBSCRIPTIONS OVERVIEW -->
      <div class="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h3 class="text-sm font-extrabold uppercase tracking-wider text-slate-100 flex items-center gap-2">
              <Layers class="w-4 h-4 text-sky-400" />
              {{ t('billing.activeSubscriptionsTitle') }}
            </h3>
            <p class="text-xs text-slate-400">
              Subscription lifecycle states and contracted modules.
            </p>
          </div>

          <NButton size="small" secondary @click="loadBillingData" :loading="isLoadingBilling" class="text-xs font-semibold">
            <template #icon><RefreshCw class="w-3.5 h-3.5" /></template>
            Refresh Status
          </NButton>
        </div>

        <div v-if="activeSubscriptions.length === 0" class="p-6 text-center rounded-2xl bg-slate-950/60 border border-slate-800/80 text-xs text-slate-400">
          {{ t('billing.noActiveSubs') }}
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="sub in activeSubscriptions"
            :key="sub.id"
            class="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex flex-col justify-between space-y-3"
          >
            <div class="space-y-1.5">
              <div class="flex items-start justify-between gap-2">
                <h4 class="text-sm font-bold text-slate-100">{{ sub.serviceName || sub.serviceId.replace(/_/g, ' ') }}</h4>

                <!-- Status Badges including cancel_wait badge -->
                <NTag
                  v-if="sub.status === 'cancel_wait'"
                  type="warning"
                  size="small"
                  :bordered="false"
                >
                  <span class="flex items-center gap-1">
                    <Clock class="w-3 h-3" />
                    {{ t('billing.pendingCancelBadge') }}
                  </span>
                </NTag>
                <NTag
                  v-else
                  :type="sub.status === 'ACTIVE' ? 'success' : 'default'"
                  size="small"
                  :bordered="false"
                >
                  {{ sub.status }}
                </NTag>
              </div>

              <div class="flex items-baseline gap-2">
                <span class="text-base font-mono font-extrabold text-emerald-400">${{ sub.amount }}.00</span>
                <span class="text-[11px] text-slate-400 uppercase font-semibold">/ {{ sub.billingInterval.toLowerCase() }}</span>
              </div>
            </div>

            <div class="pt-2 border-t border-slate-800/60 space-y-1.5 text-[11px] text-slate-400">
              <div class="flex items-center justify-between">
                <span>Payer Type:</span>
                <span class="font-bold text-slate-200">{{ sub.payerType }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span>Current Period End:</span>
                <span class="font-mono text-slate-300">{{ sub.currentPeriodEnd }}</span>
              </div>

              <!-- Resume Subscription button for cancel_wait status -->
              <div v-if="sub.status === 'cancel_wait' && isAdmin" class="pt-1">
                <NButton
                  size="tiny"
                  type="warning"
                  block
                  :loading="isResumingService[sub.serviceId]"
                  @click="handleResumeSubscription({ id: sub.serviceId, name: sub.serviceName } as any)"
                  class="font-bold text-xs"
                >
                  <template #icon><RotateCcw class="w-3 h-3" /></template>
                  {{ t('billing.resumeBtn') }}
                </NButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 4. SERVICES CATALOG & SEAT ALLOCATION -->
      <div class="space-y-4">
        <!-- Team Selector Bar -->
        <div class="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xl">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-400">
              <Building2 class="w-5 h-5" />
            </div>
            <div>
              <span class="text-[10px] text-slate-400 uppercase font-bold tracking-wider">{{ t('billing.managingTeam') }}</span>
              <h2 class="text-base font-bold text-slate-100">{{ activeTeam.name }}</h2>
            </div>
          </div>

          <div class="w-full sm:w-64">
            <NSelect
              v-model:value="selectedTeamId"
              :options="teamOptions"
              size="medium"
            />
          </div>
        </div>

        <div>
          <h3 class="text-base font-bold text-slate-100">{{ t('billing.servicesCatalog') }}</h3>
          <p class="text-xs text-slate-400">
            Configure payment models and manage service seat allocations for active modules.
          </p>
        </div>

        <!-- Services Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div
            v-for="service in services"
            :key="service.id"
            class="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl flex flex-col justify-between relative overflow-hidden"
          >
            <div class="space-y-4">
              <!-- Service Header -->
              <div class="flex items-start justify-between gap-3">
                <div class="flex items-center gap-3">
                  <div
                    :class="[
                      'w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg shadow-md',
                      service.id === 'VIDEO_ANALYTICS' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' :
                      service.id === 'SKILL_TRAINING' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' :
                      'bg-sky-500/15 text-sky-400 border border-sky-500/30'
                    ]"
                  >
                    <Video v-if="service.id === 'VIDEO_ANALYTICS'" class="w-6 h-6" />
                    <Dumbbell v-else-if="service.id === 'SKILL_TRAINING'" class="w-6 h-6" />
                    <Sparkles v-else class="w-6 h-6" />
                  </div>
                  <div>
                    <h4 class="text-base font-extrabold text-slate-100">{{ service.name }}</h4>
                    <span class="text-xs font-mono font-bold text-sky-400">${{ service.priceMonthly }} / month</span>
                  </div>
                </div>

                <div class="flex flex-col items-end gap-1">
                  <NTag
                    v-if="isServiceCancelWait(service.id)"
                    type="warning"
                    size="small"
                    :bordered="false"
                  >
                    Pending Cancellation
                  </NTag>
                  <AppBadge
                    v-else
                    :variant="isServiceActiveForTeam(service.id) ? 'success' : 'slate'"
                  >
                    {{ isServiceActiveForTeam(service.id) ? (getServicePaymentType(service.id) === 'TEAM' ? 'Team Active' : 'Open to Players') : 'Inactive' }}
                  </AppBadge>
                </div>
              </div>

              <p class="text-xs text-slate-300 leading-relaxed">
                {{ service.description }}
              </p>

              <!-- Payment Constraint Notice for TEAM_ONLY services -->
              <div v-if="isTeamOnlyService(service)" class="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center gap-2 text-[11px] text-amber-300 font-semibold">
                <AlertTriangle class="w-4 h-4 shrink-0 text-amber-400" />
                <span>{{ service.id === 'VIDEO_ANALYTICS' ? t('billing.videoAnalyticsRule') : 'This service is configured for Team Master payment only.' }}</span>
              </div>

              <!-- Payment Type Selector (Only Admin can modify) -->
              <div v-if="isAdmin" class="p-3.5 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
                <span class="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">Payment Model:</span>
                <div class="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    @click="handleServicePaymentTypeChange(service.id, 'TEAM')"
                    :class="[
                      'px-3 py-2 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5',
                      getServicePaymentType(service.id) === 'TEAM'
                        ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40 shadow-sm'
                        : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-slate-200'
                    ]"
                  >
                    <Building2 class="w-3.5 h-3.5" />
                    <span>Team Payment</span>
                  </button>

                  <button
                    type="button"
                    :disabled="isTeamOnlyService(service)"
                    @click="handleServicePaymentTypeChange(service.id, 'INDIVIDUAL')"
                    :class="[
                      'px-3 py-2 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5',
                      isTeamOnlyService(service)
                        ? 'opacity-40 cursor-not-allowed bg-slate-900 text-slate-600 border border-slate-800'
                        : getServicePaymentType(service.id) === 'INDIVIDUAL'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm'
                        : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-slate-200'
                    ]"
                  >
                    <Users class="w-3.5 h-3.5" />
                    <span>Individual Payment</span>
                  </button>
                </div>
              </div>

              <!-- Feature Bullet points -->
              <ul class="space-y-1.5 text-xs text-slate-400">
                <li v-for="feat in service.features" :key="feat" class="flex items-center gap-2">
                  <CheckCircle2 class="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span>{{ feat }}</span>
                </li>
              </ul>
            </div>

            <!-- Service Action Controls -->
            <div class="mt-6 pt-4 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3">
              <!-- Seat Allocations Button (Both ADMIN and COACH can allocate seats) -->
              <div>
                <NButton
                  v-if="canAllocateSeats && isServiceActiveForTeam(service.id)"
                  size="small"
                  secondary
                  type="info"
                  @click="openSeatAllocator(service)"
                  class="font-bold text-xs"
                >
                  <template #icon><UserCheck class="w-3.5 h-3.5" /></template>
                  {{ t('billing.manageSeatsBtn') }}
                </NButton>
              </div>

              <!-- Subscription Buttons (Only ADMIN can upgrade/cancel) -->
              <div v-if="isAdmin">
                <template v-if="isServiceCancelWait(service.id)">
                  <NButton
                    type="warning"
                    :loading="isResumingService[service.id]"
                    @click="handleResumeSubscription(service)"
                    size="medium"
                    class="font-bold text-xs"
                  >
                    {{ t('billing.resumeBtn') }}
                  </NButton>
                </template>

                <template v-else-if="getServicePaymentType(service.id) === 'TEAM'">
                  <NButton
                    :type="isServiceActiveForTeam(service.id) ? 'default' : 'primary'"
                    :loading="isSubscribingService[service.id] || isCancelingService[service.id]"
                    @click="handleTeamAdminSubscribe(service)"
                    size="medium"
                    class="font-bold text-xs shadow-md"
                  >
                    {{ isServiceActiveForTeam(service.id) ? (service.isMandatory ? 'Core Active' : t('billing.cancelTeamPlan')) : t('billing.subscribeTeamPlan') }}
                  </NButton>
                </template>

                <template v-else>
                  <NButton
                    :type="isServiceActiveForTeam(service.id) ? 'default' : 'primary'"
                    :loading="isSubscribingService[service.id]"
                    @click="handleTeamAdminSubscribe(service)"
                    size="medium"
                    class="font-bold text-xs shadow-md"
                  >
                    {{ isServiceActiveForTeam(service.id) ? 'Close Player Access' : 'Make Available to Users' }}
                  </NButton>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 5. INVOICE HISTORY TABLE (InvoiceTable component with n-tabs) -->
      <InvoiceTable />
    </div>

    <!-- ========================================================================= -->
    <!-- PLAYER / GUARDIAN VIEW -->
    <!-- ========================================================================= -->
    <div v-else class="space-y-8">
      <!-- Athlete Dues Status Card -->
      <div class="p-6 rounded-3xl bg-gradient-to-r from-sky-950 via-slate-900 to-indigo-950 border border-sky-500/20 shadow-2xl flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div class="flex items-center gap-4">
          <div class="w-14 h-14 rounded-2xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-sky-400 font-extrabold text-xl">
            #18
          </div>
          <div>
            <div class="flex items-center gap-2">
              <h2 class="text-xl font-black text-white">{{ currentUser.fullName }} &bull; {{ t('billing.memberDuesPortal') }}</h2>
              <AppBadge variant="success">{{ t('billing.goodStanding') }}</AppBadge>
            </div>
            <p class="text-xs text-slate-300 mt-1">
              {{ t('billing.enrolledWith', { team: activeTeam.name, org: activeTenant.name }) }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <div class="p-3 bg-slate-900/80 rounded-xl border border-slate-800 text-center">
            <span class="text-[10px] text-slate-400 uppercase font-bold block">{{ t('billing.seasonDues') }}</span>
            <span class="text-sm font-mono font-bold text-emerald-400">$150.00 (Paid)</span>
          </div>
        </div>
      </div>

      <!-- Services List for Player / Parent -->
      <div class="space-y-4">
        <div>
          <h3 class="text-base font-bold text-slate-100">Team Services & Individual Subscriptions</h3>
          <p class="text-xs text-slate-400">
            Services configured by your organization. Individual subscriptions are billed monthly.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            v-for="service in services"
            :key="service.id"
            class="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl flex flex-col justify-between space-y-4"
          >
            <div>
              <div class="flex items-start justify-between">
                <div class="flex items-center gap-3">
                  <div
                    :class="[
                      'w-10 h-10 rounded-xl flex items-center justify-center',
                      service.id === 'VIDEO_ANALYTICS' ? 'bg-amber-500/20 text-amber-400' :
                      service.id === 'SKILL_TRAINING' ? 'bg-emerald-500/20 text-emerald-400' :
                      'bg-sky-500/20 text-sky-400'
                    ]"
                  >
                    <Video v-if="service.id === 'VIDEO_ANALYTICS'" class="w-5 h-5" />
                    <Dumbbell v-else-if="service.id === 'SKILL_TRAINING'" class="w-5 h-5" />
                    <Sparkles v-else class="w-5 h-5" />
                  </div>
                  <div>
                    <h4 class="text-sm font-bold text-slate-100">{{ service.name }}</h4>
                    <template v-if="getServicePaymentType(service.id) === 'TEAM'">
                      <span class="text-xs font-mono font-bold text-sky-400">Team Sponsored (Free for You)</span>
                    </template>
                    <template v-else>
                      <span class="text-xs font-mono font-bold text-emerald-400">${{ service.priceMonthly }}.00 / month</span>
                    </template>
                  </div>
                </div>

                <AppBadge
                  :variant="
                    getServicePaymentType(service.id) === 'TEAM'
                      ? (isServiceActiveForTeam(service.id) ? 'primary' : 'slate')
                      : 'success'
                  "
                >
                  {{
                    getServicePaymentType(service.id) === 'TEAM'
                      ? (isServiceActiveForTeam(service.id) ? 'Team Covered' : 'Not Subscribed')
                      : 'Monthly Plan'
                  }}
                </AppBadge>
              </div>

              <p class="text-xs text-slate-300 mt-3">
                {{ service.description }}
              </p>
            </div>

            <div class="pt-3 border-t border-slate-800 flex items-center justify-between">
              <template v-if="getServicePaymentType(service.id) === 'TEAM'">
                <span v-if="isServiceActiveForTeam(service.id)" class="text-[11px] text-emerald-400 font-semibold flex items-center gap-1">
                  <CheckCircle2 class="w-3.5 h-3.5" /> Full Access Granted by Team
                </span>
                <span v-else class="text-[11px] text-slate-500">
                  Not activated by team administrator
                </span>
              </template>

              <template v-else>
                <div class="flex items-center justify-between w-full">
                  <span class="text-[11px] text-slate-400">Enforce Monthly Billing</span>
                  <NButton
                    type="primary"
                    size="small"
                    @click="openPlayerCheckout(service)"
                    class="font-bold text-xs shadow-md"
                  >
                    Subscribe (${{ service.priceMonthly }}/mo)
                  </NButton>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Invoices for Player -->
      <InvoiceTable />
    </div>

    <!-- ========================================================================= -->
    <!-- MODALS & DRAWERS -->
    <!-- ========================================================================= -->

    <!-- Seat Allocator Modal (Domain Component) -->
    <SeatAllocatorModal
      v-model:show="isSeatAllocatorOpen"
      :service-id="selectedServiceForSeats"
      :service-name="selectedServiceNameForSeats"
      :team-id="activeTeam.id"
      @updated="loadBillingData"
    />

    <!-- 1. STRIPE PAYMENT ELEMENT SETUP MODAL -->
    <AppModal
      v-model="isStripeSetupModalOpen"
      :title="pendingServiceToSubscribe ? `Set Up Payment Method for ${pendingServiceToSubscribe.name}` : t('billing.selectPaymentMethod')"
      :subtitle="activeTenant.name"
      max-width="lg"
    >
      <div class="space-y-5">
        <div class="flex items-center justify-between text-xs text-slate-400 pb-2 border-b border-slate-800">
          <span>Stripe Multi-Method Setup (Card, Bank Transfer, Konbini):</span>
          <NButton size="small" secondary :loading="isSettingUpPaymentMethod" @click="launchHostedStripeSetup">
            <template #icon><ExternalLink class="w-3.5 h-3.5 text-sky-400" /></template>
            Hosted Stripe Checkout
          </NButton>
        </div>

        <div class="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-3 min-h-[160px]">
          <div v-if="isMountingStripeElements" class="py-12 text-center text-xs text-slate-400 space-y-3">
            <NSpin size="medium" />
            <p>{{ t('billing.loadingStripeElements') }}</p>
          </div>

          <div v-else-if="stripeMountError" class="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400 flex items-start gap-2">
            <AlertTriangle class="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p class="font-bold">Stripe Initialization Notice</p>
              <p class="mt-1 text-[11px] text-slate-300">{{ stripeMountError }}</p>
              <NButton size="small" type="primary" class="mt-3 font-bold text-xs" @click="launchHostedStripeSetup">
                Open Hosted Stripe Setup Instead
              </NButton>
            </div>
          </div>

          <div id="stripe-payment-element-mount"></div>
        </div>

        <div class="flex items-center justify-between pt-2 border-t border-slate-800">
          <span class="text-[11px] text-slate-500 flex items-center gap-1">
            <Lock class="w-3.5 h-3.5 text-emerald-400" />
            {{ t('billing.stripeSecure256') }}
          </span>

          <div class="flex items-center gap-3">
            <NButton quaternary @click="isStripeSetupModalOpen = false">
              {{ t('common.cancel') }}
            </NButton>
            <NButton
              type="primary"
              :loading="isSettingUpPaymentMethod || (pendingServiceToSubscribe ? isSubscribingService[pendingServiceToSubscribe.id] : false)"
              @click="handleConfirmStripeSetup"
              class="font-bold text-xs shadow-lg shadow-sky-500/20"
            >
              {{ pendingServiceToSubscribe ? t('billing.proceedToStripe') : t('billing.confirmPaymentMethod') }}
            </NButton>
          </div>
        </div>
      </div>
    </AppModal>

    <!-- 2. TEAM ADMIN PAYLINK MODAL -->
    <AppModal
      v-model="isPaylinkModalOpen"
      :title="t('billing.paylinkGenTitle')"
      :subtitle="activeTeam.name"
      max-width="md"
    >
      <div class="space-y-4">
        <p class="text-xs text-slate-300">
          {{ t('billing.paylinkGenSubtitle') }}
        </p>

        <div class="p-3 bg-slate-950 border border-slate-800 rounded-2xl flex items-center justify-between gap-3">
          <span class="font-mono text-xs text-sky-400 truncate flex-1">{{ generatedPaylink }}</span>
          <NButton size="small" secondary @click="copyGeneratedLink">
            <template #icon><Copy class="w-3.5 h-3.5" /></template>
            {{ t('billing.copyBtn') }}
          </NButton>
        </div>

        <div class="pt-3 flex justify-end">
          <NButton type="primary" @click="isPaylinkModalOpen = false" class="font-bold text-xs">
            {{ t('billing.doneBtn') }}
          </NButton>
        </div>
      </div>
    </AppModal>

    <!-- 3. PLAYER CHECKOUT MODAL -->
    <AppModal
      v-model="isPlayerStripeCheckoutOpen"
      :title="t('billing.stripeModalTitle')"
      :subtitle="selectedServiceToPay ? `${selectedServiceToPay.name} - $${selectedServiceToPay.priceMonthly}/mo` : ''"
      max-width="md"
    >
      <div class="space-y-4">
        <div class="p-3.5 bg-slate-950 border border-slate-800 rounded-2xl flex items-center justify-between text-xs">
          <span class="text-slate-400">{{ t('billing.monthlyDuesLabel') }}</span>
          <span class="text-lg font-mono font-extrabold text-emerald-400">${{ selectedServiceToPay?.priceMonthly || 49 }}.00</span>
        </div>

        <p class="text-xs text-slate-300">
          You will be redirected to Stripe's secure hosted checkout page to complete your individual subscription for <strong>{{ selectedServiceToPay?.name }}</strong>.
        </p>

        <div class="pt-3 flex items-center justify-between text-[11px] text-slate-500">
          <span class="flex items-center gap-1"><Lock class="w-3.5 h-3.5 text-emerald-400" /> 256-bit Stripe Encryption</span>
        </div>

        <div class="pt-2 flex justify-end gap-3 border-t border-slate-800">
          <NButton quaternary @click="isPlayerStripeCheckoutOpen = false">
            {{ t('common.cancel') }}
          </NButton>
          <NButton
            type="primary"
            :loading="playerPaymentProcessing"
            @click="processPlayerStripePayment"
            class="font-bold text-xs shadow-lg shadow-sky-500/20"
          >
            {{ t('billing.payWithStripe') }}
          </NButton>
        </div>
      </div>
    </AppModal>
  </div>
</template>
